/**
 * Canonical sales permission strings, copied verbatim from
 * openapi/generated/route_policy.gen.go. Controls are gated on the exact
 * permission the route demands, so no rendered button can 403.
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ShieldX } from "lucide-react";
import { Card, CardContent } from "@erp/ui";

import { ModuleBoundary } from "@/features/apps/module-boundary";
import { useSession } from "@/lib/session/use-session";

export const SALES_PERMISSIONS = {
  orderRead: "sales.order.read",
  /** Purpose-specific: reading it is audited, and so is being denied it. */
  orderPiiRead: "sales.order.pii.read",
  quoteRead: "sales.quote.read",
  quoteWrite: "sales.quote.write",
  quoteConvert: "sales.quote.convert",
  fulfilmentRead: "sales.fulfilment.read",
  fulfilmentAllocate: "sales.fulfilment.allocate",
  fulfilmentExecute: "sales.fulfilment.execute",
} as const;

export type SalesPermission = (typeof SALES_PERMISSIONS)[keyof typeof SALES_PERMISSIONS];

/** Direct-route authorization: denied pages never mount or issue queries. */
export function SalesPermissionBoundary({
  permission,
  children,
}: {
  permission: SalesPermission;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { hasPermission, hasModule } = useSession();

  // The app comes before the grant, and in that order deliberately. If the
  // company has not installed this app, saying "you lack permission" sends
  // somebody to an administrator who cannot help — the fix is to install it,
  // not to be granted something. The server answers the same way round.
  if (!hasModule("sales")) return <ModuleBoundary app="sales">{children}</ModuleBoundary>;

  if (hasPermission(permission)) return <>{children}</>;

  return (
    <div className="flex min-h-[22rem] items-center justify-center p-4">
      <Card
        role="alert"
        aria-labelledby="sales-forbidden-title"
        className="w-full max-w-xl border-destructive/30 bg-card/95 shadow-sm"
        data-testid="sales-route-forbidden"
      >
        <CardContent className="flex gap-4 p-6 sm:p-8">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive">
            <ShieldX className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 space-y-2">
            <h1 id="sales-forbidden-title" className="text-xl font-semibold tracking-tight">
              {t("sales.permissions.title")}
            </h1>
            <p className="text-sm text-muted-foreground">{t("errors.forbidden", { permission })}</p>
            <code dir="ltr" className="block w-fit rounded border bg-muted px-2 py-1 text-xs">
              {permission}
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
